/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import TableRow from '@material-ui/core/TableRow';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useFees } from './useFees';
import { amountFormatter } from './fees.helper';
import { FeeCheckboxCell } from './cells/FeeCheckboxCell';
import { FeeTextCell } from './cells/FeeTextCell';
import { FeeBarcodeCell } from './cells/FeeBarcodeCell';
import { FeeInputNumberCell } from './cells/FeeInputNumberCell';
import { FeeSwitchCell } from './cells/FeeSwitchCell';
import { CURRENCY_POSITION_TYPE, FeeCellType } from '../../../shared/enums/FeeCellType';
import { selectVersion } from '../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../shared/utils/versionUtils';

const useStyles = makeStyles(() => ({
  subtotalCell: {
    maxWidth: '12.5rem',
    '& > p': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));

export default function FeeTableRow({
  fee,
  formData,
  disabled,
  uiSchema,
  feesSelection,
  formContext,
  onBlur,
  onChange,
  registry,
}) {
  const {
    onClickCheckbox,
    onChangeQuantity,
    onBlurCheckbox,
    onBlurQuantity,
    calculateSubtotal,
    onChangeBarcode,
    getFeeAdditionalConfig,
    onChangeLateFee,
    onChangeAdditionalField,
  } = useFees(formData, feesSelection, formContext, onBlur, onChange, registry);

  const classes = useStyles();
  const feeSelected =
    formData?.selectedFees &&
    Object.keys(formData?.selectedFees).length > 0 &&
    formData?.selectedFees?.find(feeItem => feeItem.code === fee.code);
  const applicableDiscounts = formData?.applicableDiscounts?.find(feeItem => feeItem.code === fee.code);
  const applicableSurcharges = formData?.applicableSurcharges?.find(feeItem => feeItem.code === fee.code);
  const checked = !!feeSelected;
  const currency = !feesSelection.hideCurrency ? feesSelection.currencySymbol : '';
  const history = useHistory();
  const isBarcodeDisabled = !feeSelected || uiSchema?.barcodeDisabled || history.location?.pathname?.includes('view');
  const isLateFeeDisabled = !feeSelected || uiSchema?.lateFeeDisabled;
  const feeAdditionalConfig = getFeeAdditionalConfig(fee.code);
  const feeTxt = amountFormatter().format(feeAdditionalConfig.amount);
  const feeTextWithCurrency =
    feesSelection?.currencyPosition === CURRENCY_POSITION_TYPE?.AFTER
      ? `${feeTxt} ${currency}`
      : `${currency} ${feeTxt}`;
  const feeDescription = feeAdditionalConfig.descriptionKey || fee.text;

  const selectedVersion = useSelector(selectVersion);

  // columns are rendered by column type to have a dynamic table structure
  return (
    <TableRow key={`row-${fee.code}`} style={{ verticalAlign: 'baseline' }}>
      {feesSelection.columns.map(({ type, field }) => {
        switch (type) {
          case FeeCellType.CHECKBOX.value:
            return (
              <FeeCheckboxCell
                key={`${fee.code}-${type}`}
                fee={fee}
                checked={checked}
                disabled={disabled}
                onClick={onClickCheckbox(fee)}
                onBlur={onBlurCheckbox}
                disabledTooltip={fee.disabledTooltip}
              />
            );

          case FeeCellType.TEXT.value:
            return <FeeTextCell key={`${fee.code}-${type}`} text={<Trans i18nKey={feeDescription} />} />;

          case FeeCellType.GENERIC.value:
            return <FeeTextCell key={`${fee.code}-${type}-${field}`} text={<Trans i18nKey={feeSelected[field]} />} />;

          case FeeCellType.GENERIC_INPUT_NUMBER.value:
            return (
              <FeeInputNumberCell
                key={`${fee.input}-${fee.code}-${type}-${field}`}
                value={feeSelected && feeSelected.hasOwnProperty(field) ? feeSelected[field] : ''}
                fee={fee}
                disabled={!feeSelected || disabled}
                onChange={onChangeAdditionalField(fee, field)}
                {...dataTestId({ value: `feesRowQuantity-NP--${selectedVersion.value}` })}
              />
            );

          case FeeCellType.LATEFEE.value: {
            return (
              <FeeSwitchCell
                key={`${fee.code}-${type}`}
                fee={fee}
                color="primary"
                checked={feeSelected?.isLate}
                disabled={isLateFeeDisabled}
                onChange={onChangeLateFee(fee)}
              />
            );
          }

          case FeeCellType.BARCODE.value:
            return (
              <FeeBarcodeCell
                key={`${fee.barcode}-${fee.code}-${type}`}
                fee={fee}
                feeSelected={feeSelected}
                disabled={isBarcodeDisabled}
                uiSchema={uiSchema}
                onChange={onChangeBarcode(fee)}
              />
            );

          case FeeCellType.PRICE.value:
            return (
              <FeeTextCell
                key={`${fee.code}-${type}`}
                text={feeTextWithCurrency}
                {...dataTestId({ value: `feesRowPriceValue-NP--${selectedVersion.value}` })}
              />
            );

          case FeeCellType.DISCOUNT.value: {
            const discount = applicableDiscounts?.discount || fee.discount || 0;
            return (
              <FeeTextCell
                key={`${fee.code}-${type}`}
                text={`${discount}%`}
                {...dataTestId({ value: `feesRowDiscountValue-NP--${selectedVersion.value}` })}
              />
            );
          }

          case FeeCellType.SURCHARGE.value: {
            const surcharge = applicableSurcharges?.surcharge || fee.surcharge || 0;
            return (
              <FeeTextCell
                key={`${fee.code}-${type}`}
                text={`${surcharge}%`}
                {...dataTestId({ value: `feesRowSurchargeValue-NP--${selectedVersion.value}` })}
              />
            );
          }

          case FeeCellType.QUANTITY.value:
            return fee?.showText !== false ? (
              <FeeTextCell
                key={`${fee.input}-${fee.code}-${type}`}
                text={fee?.quantity || feeSelected?.quantity}
                {...dataTestId({ value: `feesRowQuantity-NP--${selectedVersion.value}` })}
              />
            ) : (
              <FeeInputNumberCell
                key={`${fee.input}-${fee.code}-${type}`}
                value={feeSelected?.quantity || ''}
                fee={fee}
                disabled={!feeSelected || disabled}
                onChange={onChangeQuantity(fee)}
                onBlur={onBlurQuantity(fee)}
                {...dataTestId({ value: `feesRowQuantity-NP--${selectedVersion.value}` })}
              />
            );

          case FeeCellType.QUANTITYEDITABLE.value:
            return fee.quantity ? (
              <FeeTextCell
                key={`${fee.input}-${fee.code}-${type}`}
                text={feeSelected ? fee.quantity : ''}
                {...dataTestId({ value: `feesRowQuantity-NP--${selectedVersion.value}` })}
              />
            ) : (
              <FeeInputNumberCell
                key={`${fee.input}-${fee.code}-${type}`}
                value={feeSelected?.quantity || ''}
                fee={fee}
                onChange={onChangeQuantity(fee)}
                onBlur={onBlurQuantity(fee)}
                {...dataTestId({ value: `feesRowQuantity-NP--${selectedVersion.value}` })}
              />
            );

          case FeeCellType.SUBTOTAL.value:
            return (
              <FeeTextCell
                className={classes.subtotalCell}
                key={`${fee.code}-${type}`}
                text={calculateSubtotal(feesSelection, fee.code, checked, feeSelected?.quantity || 1)}
                {...dataTestId({ value: `feesRowSubtotal-NP--${selectedVersion.value}` })}
              />
            );

          default:
            return null;
        }
      })}
    </TableRow>
  );
}
